import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { fetchWithAuth } from '../../../utils/apiAuth';
import config from '../../../config';
import { downloadCsvFile } from '../../../utils/csvDownload';
import { renderLoading, renderError } from '../components/StatusViews';
import { getStatusColor } from '../utils/subscriptionUtils';

const apiBaseUrl = config.getApiUrl();

const ACTIVITY_FILTERS = [
  { value: 'all', label: 'All members' },
  { value: 'logged_in_magazine', label: 'Logged in and Checked Magazine' },
  { value: 'logged_in_no_magazine', label: 'Logged in but haven\'t Checked Magazine' },
  { value: 'not_logged_in', label: 'Not Logged in' },
  { value: 'no_magazine_card', label: 'Have not clicked Magazine' },
  { value: 'no_read_now', label: 'Have not clicked Read Now' }
];

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const toInputDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const ist = new Date(date.getTime() + IST_OFFSET_MS);
  const year = ist.getUTCFullYear();
  const month = String(ist.getUTCMonth() + 1).padStart(2, '0');
  const day = String(ist.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const now = new Date();
  const ist = new Date(now.getTime() + IST_OFFSET_MS);
  const daysSinceSaturday = (ist.getUTCDay() + 1) % 7;
  ist.setUTCDate(ist.getUTCDate() - daysSinceSaturday);
  const year = ist.getUTCFullYear();
  const month = String(ist.getUTCMonth() + 1).padStart(2, '0');
  const day = String(ist.getUTCDate()).padStart(2, '0');
  return {
    from: `${year}-${month}-${day}`,
    to: toInputDate(now)
  };
};

const filterByActivity = (members, activityFilter) => {
  switch (activityFilter) {
    case 'logged_in_magazine':
      return members.filter((member) => member.loggedIn && member.magazineCard);
    case 'logged_in_no_magazine':
      return members.filter((member) => member.loggedIn && !member.magazineCard);
    case 'not_logged_in':
      return members.filter((member) => !member.loggedIn);
    case 'no_magazine_card':
      return members.filter((member) => !member.magazineCard);
    case 'no_read_now':
      return members.filter((member) => !member.readNow);
    default:
      return members;
  }
};

const ActivityCheck = ({ done }) => (
  done ? <CheckIcon color="success" fontSize="small" /> : null
);

const MemberAnalyticsTab = () => {
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [members, setMembers] = useState([]);
  const [weekStart, setWeekStart] = useState(null);
  const [weekEnd, setWeekEnd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [draftActivityFilter, setDraftActivityFilter] = useState('all');
  const [draftDateFrom, setDraftDateFrom] = useState(defaultRange.from);
  const [draftDateTo, setDraftDateTo] = useState(defaultRange.to);
  const [appliedActivityFilter, setAppliedActivityFilter] = useState('all');
  const [appliedDateFrom, setAppliedDateFrom] = useState(defaultRange.from);
  const [appliedDateTo, setAppliedDateTo] = useState(defaultRange.to);
  const [filtersApplied, setFiltersApplied] = useState(false);

  const loadMembers = useCallback(async (dateFrom, dateTo) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (dateFrom) params.set('from', dateFrom);
      if (dateTo) params.set('to', dateTo);

      const query = params.toString();
      const url = `${apiBaseUrl}/api/member-analytics/weekly${query ? `?${query}` : ''}`;
      const data = await fetchWithAuth(url);

      setMembers(data.members || []);
      setWeekStart(data.weekStart);
      setWeekEnd(data.weekEnd);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMembers(defaultRange.from, defaultRange.to);
  }, [defaultRange.from, defaultRange.to, loadMembers]);

  const handleApplyFilters = () => {
    if (draftDateFrom && draftDateTo && draftDateFrom > draftDateTo) {
      setError('From date must be on or before To date');
      return;
    }

    setAppliedActivityFilter(draftActivityFilter);
    setAppliedDateFrom(draftDateFrom);
    setAppliedDateTo(draftDateTo);
    setFiltersApplied(true);
    setPage(0);
    setError(null);
    loadMembers(draftDateFrom, draftDateTo);
  };

  const filteredMembers = useMemo(() => {
    let result = filterByActivity(members, appliedActivityFilter);

    if (!searchQuery.trim()) return result;
    const query = searchQuery.toLowerCase().trim();
    return result.filter((member) => (
      (member.name || '').toLowerCase().includes(query) ||
      (member.email || '').toLowerCase().includes(query) ||
      (member.phone || '').toLowerCase().includes(query)
    ));
  }, [members, appliedActivityFilter, searchQuery]);

  const summary = useMemo(() => ({
    total: filteredMembers.length,
    loggedIn: filteredMembers.filter((m) => m.loggedIn).length,
    magazineCard: filteredMembers.filter((m) => m.magazineCard).length,
    readNow: filteredMembers.filter((m) => m.readNow).length
  }), [filteredMembers]);

  const pagedMembers = filteredMembers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const downloadCSV = () => {
    if (filteredMembers.length === 0) {
      return;
    }

    const headers = [
      'Name',
      'Email',
      'Phone',
      'Subscription start',
      'Subscription end',
      'Status',
      'Logged in',
      'Magazine card',
      'Read Now'
    ];

    const activityLabel = ACTIVITY_FILTERS.find((item) => item.value === appliedActivityFilter)?.label || 'All';
    const fileName = `YoungPresso_Member_Analytics_${activityLabel.replace(/[^a-zA-Z0-9]+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;

    downloadCsvFile({
      headers,
      rows: filteredMembers.map((member) => [
        member.name || '',
        member.email || '',
        member.phone || '',
        formatDate(member.subscriptionStart),
        formatDate(member.subscriptionEnd),
        member.status || '',
        member.loggedIn ? 'Yes' : 'No',
        member.magazineCard ? 'Yes' : 'No',
        member.readNow ? 'Yes' : 'No'
      ]),
      fileName
    });
  };

  return (
    <>
      <Typography
        variant="h4"
        sx={{ mb: 1, fontSize: { xs: '1.35rem', sm: '2.125rem' } }}
      >
        Weekly report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 2, sm: 3 } }}>
        Active subscribers from {formatDate(weekStart)} to {formatDate(weekEnd)}
      </Typography>

      <Box
        sx={{
          mb: 3,
          p: { xs: 2, sm: 2.5 },
          bgcolor: '#f5f5f5',
          borderRadius: 1,
          border: '1px solid #e0e0e0'
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="member-activity-filter-label">Member activity</InputLabel>
              <Select
                labelId="member-activity-filter-label"
                value={draftActivityFilter}
                label="Member activity"
                onChange={(e) => setDraftActivityFilter(e.target.value)}
              >
                {ACTIVITY_FILTERS.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="From"
              type="date"
              value={draftDateFrom}
              onChange={(e) => setDraftDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="To"
              type="date"
              value={draftDateTo}
              onChange={(e) => setDraftDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleApplyFilters}
              sx={{ height: 40 }}
            >
              Apply Filter
            </Button>
          </Grid>
        </Grid>
        {filtersApplied && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Showing {ACTIVITY_FILTERS.find((item) => item.value === appliedActivityFilter)?.label}
            {' '}from {appliedDateFrom || '—'} to {appliedDateTo || '—'}
          </Typography>
        )}
      </Box>

      {loading ? (
        renderLoading()
      ) : error ? (
        renderError(error)
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>Active subscribers</Typography>
                  <Typography variant="h4">{summary.total}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>Logged in</Typography>
                  <Typography variant="h4">{summary.loggedIn}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>Magazine card</Typography>
                  <Typography variant="h4">{summary.magazineCard}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>Read Now</Typography>
                  <Typography variant="h4">{summary.readNow}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by email, phone, or name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              sx={{ maxWidth: 600 }}
            />
          </Box>

          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
            <Button
              variant="outlined"
              color="success"
              onClick={downloadCSV}
              disabled={filteredMembers.length === 0}
              startIcon={<span>📥</span>}
              fullWidth
              sx={{ maxWidth: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}
            >
              Download CSV ({filteredMembers.length})
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table sx={{ minWidth: 980 }} aria-label="member analytics table">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Subscription start</TableCell>
                  <TableCell>Subscription end</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Logged in</TableCell>
                  <TableCell align="center">Magazine card</TableCell>
                  <TableCell align="center">Read Now</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedMembers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                        No active subscribers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedMembers.map((member) => (
                    <TableRow
                      key={member.id || member.email || member.phone}
                      sx={member.highlighted ? { bgcolor: 'rgba(255, 107, 53, 0.08)' } : undefined}
                    >
                      <TableCell>{member.name || '-'}</TableCell>
                      <TableCell>{member.email || '-'}</TableCell>
                      <TableCell>{member.phone || '-'}</TableCell>
                      <TableCell>{formatDate(member.subscriptionStart)}</TableCell>
                      <TableCell>{formatDate(member.subscriptionEnd)}</TableCell>
                      <TableCell>
                        {member.status ? (
                          <Chip
                            label={member.status}
                            color={getStatusColor(member.status)}
                            size="small"
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center"><ActivityCheck done={member.loggedIn} /></TableCell>
                      <TableCell align="center"><ActivityCheck done={member.magazineCard} /></TableCell>
                      <TableCell align="center"><ActivityCheck done={member.readNow} /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={filteredMembers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}
    </>
  );
};

export default MemberAnalyticsTab;
